import { firingMinutes } from "@shared/cron/cronUtils";
import type { ScheduleSummary } from "@shared/types/schedules";

/**
 * Round 9 (B-28): two enabled schedules that name one connection and fire in the same minute.
 * The runner runs one import per connection at a time, so the second to be taken is skipped on
 * every such firing (`connection_busy`); nothing used to say so before it happened. A clash is
 * found from the schedules the editor and the list already load, over the next week of firings.
 */

/** The facts a clash needs from a schedule (a saved row or the editor's draft). */
export interface ClashSubject {
  id: string | null;
  cron: string | null;
  timezone: string;
  enabled: boolean;
  dataflowIds: readonly string[];
  /** Round 10 wave 2 (S3): a Once schedule's instants (ISO); its `cron` is then null. */
  runDates?: readonly string[];
}

/** The future firing minutes of a Once schedule, however far ahead (epoch minutes). */
function dateMinutes(runDates: readonly string[] | undefined, from: Date): number[] {
  return (runDates ?? [])
    .map((value) => new Date(value).getTime())
    .filter((at) => !Number.isNaN(at) && at > from.getTime())
    .map((at) => Math.floor(at / 60_000));
}

/** Whether `cron` fires in the epoch minute `minute`, in `timezone`. */
function cronFiresIn(cron: string, timezone: string, minute: number): boolean {
  const start = new Date((minute - 1) * 60_000);
  return firingMinutes(cron, timezone, start, 2 / (24 * 60), 3).includes(minute);
}

/**
 * Round 10 wave 2 (S3): whether two schedules fire in one minute. Two expressions compare their
 * next week of firings; a Once schedule tests each of its instants against the other's
 * expression, or against the other's instants.
 */
function fireTogether(a: ClashSubject, b: ClashSubject, from: Date): boolean {
  if (a.cron && b.cron) {
    const mine = new Set(firingMinutes(a.cron, a.timezone, from));
    return firingMinutes(b.cron, b.timezone, from).some((minute) => mine.has(minute));
  }
  if (!a.cron && !b.cron) {
    const mine = new Set(dateMinutes(a.runDates, from));
    return dateMinutes(b.runDates, from).some((minute) => mine.has(minute));
  }
  const [dates, cron] = a.cron ? [b, a] : [a, b];
  return dateMinutes(dates.runDates, from).some((minute) =>
    cronFiresIn(cron.cron as string, cron.timezone, minute),
  );
}

function hasFirings(subject: ClashSubject): boolean {
  return Boolean(subject.cron) || (subject.runDates?.length ?? 0) > 0;
}

export interface ScheduleClash {
  /** The other schedule. */
  other: Pick<ScheduleSummary, "id" | "name">;
  /** The connections both name, as ids. */
  sharedConnectionIds: string[];
}

function shareAny(a: readonly string[], b: readonly string[]): string[] {
  const held = new Set(a);
  return b.filter((id) => held.has(id));
}

/** The other enabled schedules that fire in the same minute as `subject` on a shared connection. */
export function findScheduleClashes(
  subject: ClashSubject,
  others: readonly ScheduleSummary[],
  from: Date = new Date(),
): ScheduleClash[] {
  if (!subject.enabled || !hasFirings(subject) || subject.dataflowIds.length === 0) return [];
  const candidates = others.filter(
    (other) =>
      other.id !== subject.id &&
      other.enabled &&
      hasFirings(other) &&
      shareAny(subject.dataflowIds, other.dataflowIds).length > 0,
  );
  if (candidates.length === 0) return [];
  const clashes: ScheduleClash[] = [];
  for (const other of candidates) {
    if (!fireTogether(subject, other, from)) continue;
    clashes.push({
      other: { id: other.id, name: other.name },
      sharedConnectionIds: shareAny(subject.dataflowIds, other.dataflowIds),
    });
  }
  return clashes;
}

/** The warning's words, naming the other schedule and, when known, the shared connection. */
export function clashSentence(
  clash: ScheduleClash,
  connectionNames: ReadonlyMap<string, string>,
): string {
  const shared = clash.sharedConnectionIds
    .map((id) => connectionNames.get(id))
    .filter((name): name is string => Boolean(name));
  const where = shared.length > 0 ? ` on ${shared.join(", ")}` : " on a connection they share";
  return `${clash.other.name} also runs${where} at the same time. Only one import per connection runs at a time, so one of the two is skipped when they coincide.`;
}
